import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Mail, Lock, Eye, EyeOff, LogIn, UserPlus, AlertCircle, CheckCircle } from 'lucide-react';

interface AuthProps {
  onAuthSuccess?: () => void;
}

export const Auth: React.FC<AuthProps> = ({ onAuthSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const clearMessage = () => setMessage(null);

  const validateForm = () => {
    if (!email || !password) {
      setMessage({ type: 'error', text: 'Email e password sono obbligatori' });
      return false;
    }

    if (password.length < 6) {
      setMessage({ type: 'error', text: 'La password deve essere di almeno 6 caratteri' });
      return false;
    }

    if (isSignUp && password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Le password non coincidono' });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ type: 'error', text: 'Inserisci un indirizzo email valido' });
      return false;
    }

    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessage();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setMessage({ type: 'error', text: 'Credenziali non valide. Verifica email e password.' });
        } else if (error.message.includes('Email not confirmed')) {
          setMessage({ type: 'error', text: 'Email non confermata. Controlla la tua casella di posta.' });
        } else {
          setMessage({ type: 'error', text: error.message });
        }
        return;
      }

      if (data.user) {
        setMessage({ type: 'success', text: 'Login effettuato con successo!' });
        // Reindirizza direttamente alla dashboard dopo il login
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Errore durante il login:', error);
      setMessage({ type: 'error', text: 'Errore di connessione. Riprova più tardi.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessage();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password: password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (error) {
        setMessage({ type: 'error', text: error.message });
        return;
      }

      if (data.user) {
        // Crea il profilo utente
        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            user_id: data.user.id,
            role: 'user',
            profile_completed: false,
            disclaimer_accepted: false,
            is_approved: true,
          });

        if (profileError) {
          console.error('Errore nella creazione del profilo:', profileError);
        }

        setMessage({
          type: 'success',
          text: 'Registrazione completata! Accesso in corso...'
        });

        // Reindirizza automaticamente alla dashboard
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000);
      }
    } catch (error) {
      console.error('Errore durante la registrazione:', error);
      setMessage({ type: 'error', text: 'Errore di connessione. Riprova più tardi.' });
    } finally {
      setLoading(false);
    }
  };

  const getMessageIcon = () => {
    switch (message?.type) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-400" />;
      case 'info':
        return <AlertCircle className="h-4 w-4 text-blue-400" />;
      default:
        return null;
    }
  };

  const getMessageStyle = () => {
    switch (message?.type) {
      case 'success':
        return 'bg-green-900/20 border-green-800 text-green-400';
      case 'error':
        return 'bg-red-900/20 border-red-800 text-red-400';
      case 'info':
        return 'bg-blue-900/20 border-blue-800 text-blue-400';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="p-3 bg-blue-400/10 rounded-2xl">
              {isSignUp ? (
                <UserPlus className="h-8 w-8 text-blue-400" />
              ) : (
                <LogIn className="h-8 w-8 text-blue-400" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-white">The Edge Dashboard</h1>
          </div>
          <p className="text-gray-400">
            {isSignUp ? 'Crea il tuo account' : 'Accedi al tuo account'}
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg border flex items-center space-x-3 ${getMessageStyle()}`}>
            {getMessageIcon()}
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        {/* Auth Form */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={isSignUp ? handleSignUp : handleLogin} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                  placeholder="mario.rossi@email.com"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  disabled={loading}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password - Only for Sign Up */}
            {isSignUp && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Conferma Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    placeholder="••••••••"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-600 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  {isSignUp ? (
                    <>
                      <UserPlus className="h-5 w-5" />
                      <span>Registrati</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="h-5 w-5" />
                      <span>Accedi</span>
                    </>
                  )}
                </>
              )}
            </button>
          </form>

          {/* Toggle between Login and Sign Up */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                clearMessage();
                setPassword('');
                setConfirmPassword('');
              }}
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              disabled={loading}
            >
              {isSignUp ? (
                <>Hai già un account? <span className="font-semibold">Accedi</span></>
              ) : (
                <>Non hai un account? <span className="font-semibold">Registrati</span></>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-gray-500">
            Accedendo accetti i nostri termini di servizio e la privacy policy
          </p>
        </div>
      </div>
    </div>
  );
};